import type { GameAssetTypesUnion } from '#imports'

export interface GameTileProps {
    game: Game
    videoEnabled?: boolean
    loadMedia?: boolean
    isInline?: boolean
    enforceVideoType?: GameTileType.VIDEO | GameTileType.VIDEO_SMALL
    isMini?: boolean
    eager?: boolean
    tileVariant?: 'tall' | 'taller'
    /**
     * Preview override for the tile background, bypassing `assetUrl`. `/preview/game-tile`
     * passes TileGen drafts as data URLs, which `@nuxt/image` hands through untouched.
     */
    backgroundSrc?: string
    /** Preview override for the logo, see `backgroundSrc`. */
    logoSrc?: string
}

export enum GameProviderSlug {
    GAMES_GLOBAL = 'games-global',
    MAGNET = 'magnet',
    EVOLUTION = 'evolution',
    PARIPLAY = 'pariplay',
    PRAGMATIC = 'pragmatic',
}

export interface Game {
    id: string
    name: string
    updated_at: string
    slug: string
    provider_slug: string
    vendor_slug: string
    sort_index: number
    tile_type: GameTileTypeUnion
    game_type: GameTypeUnion
    game_subtype: GameSubtypeUnion
    is_active: boolean
    is_new: boolean
    is_exclusive: boolean
    is_recommended: boolean
    is_staff_only: boolean
    tile_decoration?: string | null
    has_jackpot: boolean
    jackpot?: string
    superJackpot?: string
    opening_hours: GameOpeningHours
    attributes: Record<string, (string | number)>
    headline: string
    description: string
    full_description: string
    instance_id?: string
    excluded_from_bonus_funds?: boolean
    game_hero_colour?: string | null
    asset_availability?: Partial<Record<GameAssetTypesUnion, boolean | null>>
    asset_folders?: Partial<Record<GameAssetTypesUnion, string>> | null
}

interface GameOpeningHours {
    is_open: boolean
    opens_at: string | number | null
}

export enum GameTileType {
    DEFAULT = 'DEFAULT',
    VIDEO = 'VIDEO',
    VIDEO_SMALL = 'VIDEO_SMALL',
    HIDDEN = 'HIDDEN'
}

export type GameTileTypeUnion = `${GameTileType}`

export enum GameType {
    CASINO = 'CASINO',
    LIVE_CASINO = 'LIVE_CASINO',
    BINGO = 'BINGO',
    SLOTS = 'SLOTS'
}

export type GameTypeUnion = `${GameType}`

/**
 * Categories a lobby can fall back to when personalized sections are unavailable.
 * Game types render a plain game search; POPULAR/JACKPOT/OTHER are curated lists
 * (see GameList/FallbackSection.vue for the mapping).
 */
export type FallbackCategory = GameTypeUnion | 'POPULAR' | 'JACKPOT' | 'OTHER'

export enum GameSubtype {
    LIVE = 'LIVE',
    COLLECTOR = 'COLLECTOR'
}

export type GameSubtypeUnion = `${GameSubtype}`

/**
 * Where a tile sits in the list it was clicked from. `row`/`col` are one-based
 * coordinates within the rendered grid/rows; `index` is the zero-based position
 * in the list's `games` array (always exact, regardless of layout).
 */
export interface GameTilePosition {
    row: number
    col: number
    index: number
}

export type GameClickEventEmit = {
    'game-click': [game: Game, position?: GameTilePosition]
}
